import type { CustomCellRendererProps } from 'ag-grid-react'
import { Copy, Trash2 } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'
import { getStatusBadgeClassName } from '@/utils/statusConfig'
import { TextCell as SharedTextCell } from '@/components/common/TextCell'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { AssortmentPlan } from '@/types'

export { EditablePlanNameCell } from './EditablePlanNameCell'

// Re-export TextCell using the shared component with 2-line clamp for this grid
export function TextCell(props: CustomCellRendererProps<AssortmentPlan>) {
  return <SharedTextCell value={props.value as string} lineClamp={2} />
}

// Active Version Cell - shows alias with tooltip, or version count fallback
export function ActiveVersionCell(
  props: CustomCellRendererProps<AssortmentPlan>
) {
  const plan = props.data
  if (!plan) return null

  if (plan.active_version_alias) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-sm font-medium text-foreground truncate cursor-default">
              {plan.active_version_alias}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {plan.versions_count} version{plan.versions_count !== 1 ? 's' : ''}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <span className="text-sm text-foreground">
      {plan.versions_count} Version{plan.versions_count !== 1 ? 's' : ''}
    </span>
  )
}

// Status Badge Cell - colored badges using shared status config
export function StatusBadgeCell(
  props: CustomCellRendererProps<AssortmentPlan>
) {
  const plan = props.data
  if (!plan) return null

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-[92px] px-4 py-0.5 rounded text-sm font-medium',
        getStatusBadgeClassName(plan.status)
      )}
    >
      {plan.status}
    </span>
  )
}

// Actions Cell props interface
interface ActionsCellProps extends CustomCellRendererProps<AssortmentPlan> {
  onCopy: (plan: AssortmentPlan) => void
  onDelete: (plan: AssortmentPlan) => void
}

// Actions Cell - displays copy and delete icons
export function ActionsCell(props: ActionsCellProps) {
  const { onCopy, onDelete } = props
  const { userId } = useCurrentUser()
  const plan = props.data
  if (!plan) return null

  const isOwner = plan.owner_id != null && plan.owner_id === userId

  return (
    <div className="flex items-center gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onCopy(plan)}
              className="p-1.5 hover:bg-muted rounded cursor-pointer"
            >
              <Copy className="h-4 w-4 text-[#556374]" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Copy</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={isOwner ? () => onDelete(plan) : undefined}
              disabled={!isOwner}
              className={cn(
                'p-1.5 rounded',
                isOwner
                  ? 'hover:bg-muted cursor-pointer'
                  : 'opacity-40 cursor-not-allowed'
              )}
            >
              <Trash2
                className={cn(
                  'h-4 w-4',
                  isOwner ? 'text-[#9B1924]' : 'text-muted-foreground'
                )}
              />
            </button>
          </TooltipTrigger>
          {!isOwner && (
            <TooltipContent>
              Only the plan owner can delete this plan
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
